import { CreatorSchema, type Creator, type CreatorContent } from '@/entities/discovery';
import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter';
import { Collection } from '@msw/data';

export const creatorCollection = new Collection({
  schema: CreatorSchema,
});

const CREATOR_COUNT = 50;

type CreatorMockProfile = {
  name: string;
  username: string;
  categories: string[];
  keywords: string[];
  platform: SOURCE_PLATFORM_TYPE;
  followerBase: number;
  engagementRate: number;
  posts: Array<{
    caption: string;
    features: string[];
    views: number;
    likes: number;
    comments: number;
    shares: number;
  }>;
};

const creatorProfiles: CreatorMockProfile[] = [
  {
    name: 'Maya Chen',
    username: 'maya.glowlab',
    categories: ['Skincare', 'Beauty tutorial', 'GRWM'],
    keywords: ['dewy skincare routine', 'bathroom mirror setup', 'ingredient-led review'],
    platform: SOURCE_PLATFORM_TYPE.TIKTOK,
    followerBase: 184000,
    engagementRate: 6.8,
    posts: [
      {
        caption: 'Testing a ceramide barrier cream after a week of retinol irritation.',
        features: ['ceramide cream demo', 'skin barrier recovery', 'close-up texture shot'],
        views: 482000,
        likes: 38600,
        comments: 812,
        shares: 4200,
      },
      {
        caption: 'Morning glass-skin routine with SPF under makeup.',
        features: ['morning routine', 'SPF layering', 'natural bathroom light'],
        views: 318000,
        likes: 24100,
        comments: 544,
        shares: 2900,
      },
      {
        caption: 'Three products I keep repurchasing for dry winter skin.',
        features: ['repurchase list', 'dry skin recommendations', 'shelfie format'],
        views: 224000,
        likes: 16800,
        comments: 391,
        shares: 1800,
      },
    ],
  },
  {
    name: 'Jordan Ellis',
    username: 'jordanfitdaily',
    categories: ['Fitness', 'Home workout', 'Wellness'],
    keywords: ['home workout routine', 'bright apartment setup', 'low-equipment fitness'],
    platform: SOURCE_PLATFORM_TYPE.TIKTOK,
    followerBase: 96000,
    engagementRate: 5.4,
    posts: [
      {
        caption: 'A 20-minute low-impact workout for small apartments.',
        features: ['low-impact workout', 'small space setup', 'follow-along format'],
        views: 271000,
        likes: 19800,
        comments: 376,
        shares: 3100,
      },
      {
        caption: 'What I eat before morning strength training.',
        features: ['pre-workout meal', 'kitchen counter shot', 'wellness routine'],
        views: 145000,
        likes: 9300,
        comments: 218,
        shares: 1100,
      },
      {
        caption: 'Beginner dumbbell circuit with form cues on screen.',
        features: ['dumbbell circuit', 'on-screen coaching', 'beginner-friendly'],
        views: 204000,
        likes: 15100,
        comments: 289,
        shares: 2400,
      },
    ],
  },
  {
    name: 'Ari Park',
    username: 'ari.styles.studio',
    categories: ['Fashion', 'Outfit ideas', 'Minimal style'],
    keywords: ['minimal outfit transition', 'neutral wardrobe', 'indoor mirror video'],
    platform: SOURCE_PLATFORM_TYPE.INSTAGRAM,
    followerBase: 132000,
    engagementRate: 4.9,
    posts: [
      {
        caption: 'Five office outfits built from the same wide-leg trousers.',
        features: ['office outfit ideas', 'mirror transition', 'neutral palette'],
        views: 356000,
        likes: 22100,
        comments: 412,
        shares: 5200,
      },
      {
        caption: 'How I style a black tank for weekend errands and dinner.',
        features: ['day-to-night styling', 'capsule wardrobe', 'indoor mirror setup'],
        views: 198000,
        likes: 13700,
        comments: 255,
        shares: 2400,
      },
      {
        caption: 'Fall layering formula: tee, knit, structured coat.',
        features: ['layering tutorial', 'structured coat', 'warm neutral styling'],
        views: 248000,
        likes: 17400,
        comments: 301,
        shares: 3300,
      },
    ],
  },
  {
    name: 'Nina Brooks',
    username: 'nina.homeedit',
    categories: ['Home', 'Organization', 'Lifestyle'],
    keywords: ['clean home reset', 'pantry organization', 'warm kitchen lighting'],
    platform: SOURCE_PLATFORM_TYPE.TIKTOK,
    followerBase: 76000,
    engagementRate: 7.1,
    posts: [
      {
        caption: 'Sunday kitchen reset before a busy work week.',
        features: ['kitchen reset', 'cleaning routine', 'before-and-after sequence'],
        views: 189000,
        likes: 14600,
        comments: 340,
        shares: 2600,
      },
      {
        caption: 'Pantry labels and bins that actually make groceries easier.',
        features: ['pantry organization', 'product bins', 'practical home tips'],
        views: 231000,
        likes: 16200,
        comments: 287,
        shares: 4100,
      },
      {
        caption: 'A 10-minute desk reset for remote work days.',
        features: ['desk organization', 'remote work setup', 'calm productivity'],
        views: 118000,
        likes: 7800,
        comments: 164,
        shares: 900,
      },
    ],
  },
  {
    name: 'Sofia Martinez',
    username: 'sofiaeatsla',
    categories: ['Food', 'Restaurant finds', 'LA lifestyle'],
    keywords: ['restaurant review', 'casual food vlog', 'voiceover recommendation'],
    platform: SOURCE_PLATFORM_TYPE.TIKTOK,
    followerBase: 218000,
    engagementRate: 8.2,
    posts: [
      {
        caption: 'Trying the new Korean bakery everyone in Silver Lake is talking about.',
        features: ['local bakery review', 'food close-ups', 'voiceover narration'],
        views: 612000,
        likes: 49800,
        comments: 936,
        shares: 7800,
      },
      {
        caption: 'Three lunch spots near Downtown LA under $15.',
        features: ['budget lunch list', 'city guide', 'quick recommendations'],
        views: 402000,
        likes: 28900,
        comments: 613,
        shares: 6100,
      },
      {
        caption: 'Date night pasta spot with the best patio lighting.',
        features: ['restaurant ambience', 'date night recommendation', 'warm lighting'],
        views: 291000,
        likes: 20300,
        comments: 385,
        shares: 3500,
      },
    ],
  },
  {
    name: 'Kai Thompson',
    username: 'kaitechdesk',
    categories: ['Tech', 'Desk setup', 'Productivity'],
    keywords: ['desk setup review', 'creator tools', 'productivity workflow'],
    platform: SOURCE_PLATFORM_TYPE.YOUTUBE,
    followerBase: 154000,
    engagementRate: 4.1,
    posts: [
      {
        caption: 'My compact desk setup for filming, editing, and client calls.',
        features: ['desk setup tour', 'creator workflow', 'gear recommendation'],
        views: 264000,
        likes: 14200,
        comments: 498,
        shares: 1700,
      },
      {
        caption: 'Testing a budget microphone against my usual studio mic.',
        features: ['side-by-side product test', 'audio gear', 'clear comparison'],
        views: 186000,
        likes: 9100,
        comments: 622,
        shares: 1200,
      },
      {
        caption: 'The Notion dashboard I use to plan weekly content.',
        features: ['productivity system', 'screen recording', 'creator planning'],
        views: 221000,
        likes: 12700,
        comments: 318,
        shares: 2600,
      },
    ],
  },
  {
    name: 'Leah Kim',
    username: 'leahmoments',
    categories: ['Family', 'Mom life', 'Daily routine'],
    keywords: ['family routine vlog', 'mom-life product demo', 'authentic voiceover'],
    platform: SOURCE_PLATFORM_TYPE.INSTAGRAM,
    followerBase: 88000,
    engagementRate: 6.1,
    posts: [
      {
        caption: 'Packing daycare bags, snacks, and my own work tote before 8am.',
        features: ['morning family routine', 'real-life pacing', 'organizing products'],
        views: 167000,
        likes: 10400,
        comments: 276,
        shares: 1500,
      },
      {
        caption: 'The stroller organizer that finally stopped my coffee spills.',
        features: ['mom-life product demo', 'stroller accessory', 'honest review'],
        views: 142000,
        likes: 8200,
        comments: 203,
        shares: 980,
      },
      {
        caption: 'A realistic Sunday reset with laundry, groceries, and meal prep.',
        features: ['family reset', 'meal prep routine', 'authentic home footage'],
        views: 196000,
        likes: 12100,
        comments: 344,
        shares: 2100,
      },
    ],
  },
  {
    name: 'Devon Price',
    username: 'devonoutdoors',
    categories: ['Outdoor', 'Travel', 'Gear'],
    keywords: ['weekend hiking gear', 'outdoor product review', 'POV trail footage'],
    platform: SOURCE_PLATFORM_TYPE.TIKTOK,
    followerBase: 121000,
    engagementRate: 5.7,
    posts: [
      {
        caption: 'What I packed for a rainy two-mile sunrise hike.',
        features: ['hiking gear checklist', 'rainy trail footage', 'practical packing'],
        views: 238000,
        likes: 15400,
        comments: 402,
        shares: 2900,
      },
      {
        caption: 'Testing a lightweight trail jacket during a windy ridge walk.',
        features: ['outdoor apparel review', 'wind test', 'POV hiking clip'],
        views: 184000,
        likes: 10900,
        comments: 251,
        shares: 1400,
      },
      {
        caption: 'Beginner-friendly state park route with parking tips.',
        features: ['travel guide', 'beginner hike', 'map overlay'],
        views: 211000,
        likes: 13200,
        comments: 387,
        shares: 3100,
      },
    ],
  },
  {
    name: 'Iris Walker',
    username: 'iris.cleanbeauty',
    categories: ['Clean beauty', 'Makeup', 'Product review'],
    keywords: ['clean makeup tutorial', 'soft glam routine', 'ingredient-conscious beauty'],
    platform: SOURCE_PLATFORM_TYPE.TIKTOK,
    followerBase: 247000,
    engagementRate: 7.6,
    posts: [
      {
        caption: 'Soft glam with cream blush, brown liner, and no foundation.',
        features: ['soft glam tutorial', 'cream blush demo', 'clean beauty positioning'],
        views: 538000,
        likes: 42600,
        comments: 771,
        shares: 6800,
      },
      {
        caption: 'Clean mascara wear test after eight hours of errands.',
        features: ['wear test', 'mascara review', 'day-in-life format'],
        views: 309000,
        likes: 21700,
        comments: 589,
        shares: 2900,
      },
      {
        caption: 'GRWM for a client meeting with five-minute makeup.',
        features: ['GRWM format', 'quick makeup routine', 'natural window light'],
        views: 428000,
        likes: 33600,
        comments: 612,
        shares: 5400,
      },
    ],
  },
  {
    name: 'Miles Carter',
    username: 'milesmenscare',
    categories: ['Men grooming', 'Fragrance', 'Style'],
    keywords: ['men grooming routine', 'fragrance recommendation', 'bathroom mirror demo'],
    platform: SOURCE_PLATFORM_TYPE.TIKTOK,
    followerBase: 69000,
    engagementRate: 5.9,
    posts: [
      {
        caption: 'A simple beard and skin routine for dry winter mornings.',
        features: ['men grooming routine', 'beard care demo', 'bathroom mirror setup'],
        views: 126000,
        likes: 7900,
        comments: 214,
        shares: 1100,
      },
      {
        caption: 'Three everyday fragrances that do not overpower the room.',
        features: ['fragrance recommendation', 'talking-head review', 'office-friendly'],
        views: 173000,
        likes: 11800,
        comments: 341,
        shares: 2200,
      },
      {
        caption: 'How I style one overshirt three ways for travel.',
        features: ['menswear styling', 'travel capsule', 'mirror outfit video'],
        views: 99000,
        likes: 6100,
        comments: 167,
        shares: 830,
      },
    ],
  },
];

export const initMockCreatorDB = () => {
  creatorCollection.clear();

  return Array.from({ length: CREATOR_COUNT }, (_, index) =>
    creatorCollection.create(createFakeCreator(index)),
  );
};

export const createFakeCreator = (index = 0): Creator => {
  const profile = creatorProfiles[index % creatorProfiles.length];
  const cycle = Math.floor(index / creatorProfiles.length);
  const creatorId = `mock-creator-${index + 1}`;
  const username = cycle === 0 ? profile.username : `${profile.username}.${cycle + 1}`;
  const followerOffset = cycle * 13_700 + index * 850;
  const posts = profile.posts.map((post, postIndex) =>
    createMockPost({
      creatorIndex: index,
      postIndex,
      username,
      post,
    }),
  );

  return {
    creator_id: creatorId,
    search_id: `mock-search-${index + 1}`,
    nickname: profile.name,
    username,
    profile_image_url: `https://i.pravatar.cc/160?u=${encodeURIComponent(username)}`,
    followers: profile.followerBase + followerOffset,
    average_er: Number((profile.engagementRate + cycle * 0.2).toFixed(1)),
    post_categories: profile.categories,
    platform: profile.platform,
    matched_keywords: profile.keywords,
    matched_post_ids: posts.map((post) => post.id),
    matched_posts: posts,
    is_saved: false,
    excluded_date: '',
  };
};

function createMockPost({
  creatorIndex,
  postIndex,
  username,
  post,
}: {
  creatorIndex: number;
  postIndex: number;
  username: string;
  post: CreatorMockProfile['posts'][number];
}): CreatorContent {
  const id = `mock-post-${creatorIndex + 1}-${postIndex + 1}`;
  const engagementLift = creatorIndex * 2300 + postIndex * 1700;

  return {
    id,
    poster_url: `https://picsum.photos/seed/${encodeURIComponent(id)}/416/720`,
    date: new Date(Date.UTC(2026, 3, 20 - postIndex, 12, 0, 0)).toISOString(),
    content: post.caption,
    ai_matched_features: post.features,
    video_url: '',
    source_url: `https://www.tiktok.com/@${username}/video/${creatorIndex + 1}${postIndex + 1}000`,
    engagement: {
      view: post.views + engagementLift,
      like: post.likes + Math.round(engagementLift * 0.08),
      comment: post.comments + Math.round(engagementLift * 0.01),
      rate: Number(((post.likes / post.views) * 100).toFixed(1)),
      shared: post.shares + Math.round(engagementLift * 0.02),
    },
  };
}
